import type { CatalogueItemResource } from '@repo/api-client';

/**
 * What a catalogue item's kind is called on screen, and what choosing one
 * commits somebody to.
 *
 * The wire values are `PRODUCT`, `TICKET_TYPE` and `ACTIVITY`, and the API is
 * the only thing that should ever see them. Nothing here restates the union — it
 * is read off `CatalogueItemResource`, so a fourth kind added to the document
 * fails to compile in this file rather than rendering as `SOMETHING_NEW` on a
 * manager's screen. `outlet-kinds.ts` does the same for the other three-kind
 * union in this app, and the two are deliberately not merged: an outlet kind and
 * a catalogue kind share a shape and nothing else.
 */
export type CatalogueKind = CatalogueItemResource['kind'];

/**
 * The kinds, in the order they are offered.
 *
 * Product first because it is the only one that can track stock and so the only
 * one whose form has a fourth decision in it.
 */
export const CATALOGUE_KINDS: readonly CatalogueKind[] = ['PRODUCT', 'TICKET_TYPE', 'ACTIVITY'];

/**
 * `Record` rather than a function with a default, so exhaustiveness is the type
 * system's problem and not a runtime fallback nobody ever sees.
 */
const KIND_LABELS: Record<CatalogueKind, string> = {
  PRODUCT: 'Product',
  TICKET_TYPE: 'Ticket type',
  ACTIVITY: 'Activity',
};

export const labelForKind = (kind: CatalogueKind): string => KIND_LABELS[kind];

/**
 * The same kinds as a value-to-label map, for `<Select items={…}>`.
 *
 * Base UI resolves what the trigger shows from this prop and not from the
 * rendered `<SelectItem>`s, so a select given options but no `items` shows the
 * raw value — `TICKET_TYPE` rather than "Ticket type". Derived from
 * `CATALOGUE_KINDS` so the two cannot drift.
 */
export const CATALOGUE_KIND_ITEMS: ReadonlyArray<{ label: string; value: CatalogueKind }> =
  CATALOGUE_KINDS.map((kind) => ({ label: labelForKind(kind), value: kind }));

/** What each kind is, shown under the picker while it is selected. */
const KIND_HINTS: Record<CatalogueKind, string> = {
  PRODUCT: 'Something sold off a shelf — a bottle of cola, a packet of seeds, a doll.',
  TICKET_TYPE: 'An entry ticket sold at the gate — adult, child, camera.',
  ACTIVITY: 'A ride or a session somebody pays to take part in.',
};

export const hintForKind = (kind: CatalogueKind): string => KIND_HINTS[kind];

/**
 * Whether a kind may count stock. `PRODUCT` and nothing else (FR-212).
 *
 * The API refuses the rest with `CATALOGUE_STOCK_ON_NON_PRODUCT`, a 422. This
 * is what lets the form disable the switch instead of collecting the answer and
 * being told off for it — `docs/ui-conventions.md` asks for exactly that, and a
 * disabled control that says why is a better teacher than a refusal.
 */
export const mayTrackStock = (kind: CatalogueKind): boolean => kind === 'PRODUCT';

/**
 * Whether an item has been retired.
 *
 * The nullable-timestamp annotation gap this was written around (`#209`) is
 * **closed** as of the 10 Aug 2026 regeneration: the field is a real
 * `string | null` and a screen may now say when. Nothing needs unwinding — the
 * predicate is still the right shape for the question it answers — but a screen
 * that wants the moment no longer has to pretend it cannot have it.
 */
export const isRetired = (item: CatalogueItemResource): boolean =>
  item.retiredAt !== null && item.retiredAt !== undefined;
